"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.teamsDevPortalClient = exports.TeamsDevPortalClient = void 0;
const tslib_1 = require("tslib");
const hooks_1 = require("@feathersjs/hooks");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const axios_1 = tslib_1.__importDefault(require("axios"));
const constants_1 = require("../common/constants");
const globalVars_1 = require("../common/globalVars");
const localizeUtils_1 = require("../common/localizeUtils");
const retryHandler_1 = require("../common/retryHandler");
const telemetry_1 = require("../common/telemetry");
const wrappedAxiosClient_1 = require("../common/wrappedAxiosClient");
const commonConstant_1 = require("../component/constant/commonConstant");
const constants_2 = require("../component/driver/teamsApp/constants");
const errors_1 = require("../component/driver/teamsApp/errors");
const ManifestUtils_1 = require("../component/driver/teamsApp/utils/ManifestUtils");
const IBotRegistration_1 = require("../component/resource/botService/appStudio/interfaces/IBotRegistration");
const common_1 = require("../component/resource/botService/common");
const constants_3 = require("../component/resource/botService/constants");
const errors_2 = require("../component/resource/botService/errors");
const messages_1 = require("../component/resource/botService/messages");
const strings_1 = require("../component/resource/botService/strings");
const teamsApp_1 = require("../error/teamsApp");
const signInAudience_1 = require("../component/driver/aad/interface/signInAudience");
const constants_4 = require("../component/driver/aad/utility/constants");
const signInAudienceNotAllowedError_1 = require("../component/driver/aad/error/signInAudienceNotAllowedError");
class TeamsDevPortalClient {
    regionEndpoint;
    setRegionEndpoint(regionEndpoint) {
        this.regionEndpoint = regionEndpoint;
    }
    async setRegionEndpointByToken(authSvcToken) {
        if ((0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.TDP) === "https://dev-int.teams.microsoft.com") {
            // Do not set region for INT env
            return;
        }
        const requester = wrappedAxiosClient_1.WrappedAxiosClient.create({
            baseURL: (0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.AuthSvc),
        });
        requester.defaults.headers.common["Authorization"] = `Bearer ${authSvcToken}`;
        requester.defaults.headers.common["Client-Source"] = "teamstoolkit";
        const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/v1.0/users/region"));
        this.regionEndpoint = response?.data?.regionGtms?.teamsDevPortal;
    }
    getEndpoint() {
        return this.regionEndpoint || (0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.TDP);
    }
    /**
     * Creates a new axios instance to call app studio to prevent setting the accessToken on global instance.
     * @param {string}  token
     * @returns {AxiosInstance}
     */
    createRequesterWithToken(token) {
        const instance = wrappedAxiosClient_1.WrappedAxiosClient.create({
            baseURL: this.getEndpoint(),
        });
        instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        instance.defaults.headers.common["Client-Source"] = "teamstoolkit";
        return instance;
    }
    /**
     * Import an app registration in app studio with the given archived file and returns the app definition.
     * @param {string}  token - access token
     * @param {Buffer}  file - Zip file with manifest.json and two icons
     * @param {boolean} overwrite - whether to overrite the app if it already exists
     * @returns {Promise<AppDefinition>}
     */
    async importApp(token, file, overwrite = false) {
        try {
            const requester = this.createRequesterWithToken(token);
            globalVars_1.TOOLS.logProvider.debug(`Sent API Request: ${this.getEndpoint()}/api/appdefinitions/v2/import`);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/appdefinitions/v2/import`, file, {
                headers: { "Content-Type": "application/zip" },
                params: {
                    overwriteIfAppAlreadyExists: overwrite,
                },
            }));
            if (response && response.data) {
                const app = response.data;
                globalVars_1.TOOLS.logProvider.debug(`Received data from Developer Portal: ${JSON.stringify(app)}`);
                return app;
            }
            else {
                throw this.wrapException(new Error("cannot create teams app"), constants_2.APP_STUDIO_API_NAMES.CREATE_APP);
            }
        }
        catch (e) {
            if (e.response?.status === 409) {
                throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_APP, errors_1.AppStudioError.TeamsAppCreateConflictError.name, errors_1.AppStudioError.TeamsAppCreateConflictError.message()[0], errors_1.AppStudioError.TeamsAppCreateConflictError.message()[1], true, constants_1.HelpLinks.SwitchTenant);
            }
            // Corner case: The provided app ID conflict with an existing published app
            // See Developer Portal PR: 507264
            if (e.response?.status == 422 &&
                e.response?.data.includes("App already exists and published")) {
                throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_APP, errors_1.AppStudioError.TeamsAppCreateConflictWithPublishedAppError.name, errors_1.AppStudioError.TeamsAppCreateConflictWithPublishedAppError.message()[0], errors_1.AppStudioError.TeamsAppCreateConflictWithPublishedAppError.message()[1], true);
            }
            // Corner case: App Id must be a GUID
            if (e.response?.status === commonConstant_1.HttpStatusCode.BAD_REQUEST &&
                e.response?.data.includes("App Id must be a GUID")) {
                const manifest = ManifestUtils_1.manifestUtils.extractManifestFromArchivedFile(file);
                if (manifest.isErr()) {
                    throw manifest.error;
                }
                else {
                    const teamsAppId = manifest.value.id;
                    throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_APP, errors_1.AppStudioError.InvalidTeamsAppIdError.name, errors_1.AppStudioError.InvalidTeamsAppIdError.message(teamsAppId)[0], errors_1.AppStudioError.InvalidTeamsAppIdError.message(teamsAppId)[1], true);
                }
            }
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_APP);
        }
    }
    async listApps(token) {
        if (!this.regionEndpoint)
            throw new Error("Failed to get region");
        let requester;
        try {
            requester = this.createRequesterWithToken(token);
            globalVars_1.TOOLS.logProvider.debug(`Sent API Request: GET ${this.regionEndpoint}/api/appdefinitions`);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/appdefinitions`));
            const apps = response?.data;
            if (apps) {
                return apps;
            }
            else {
                globalVars_1.TOOLS.logProvider.error("Cannot get the app definitions");
            }
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.LIST_APPS);
        }
        throw this.wrapException(new Error("cannot get the app definitions"), constants_2.APP_STUDIO_API_NAMES.LIST_APPS);
    }
    async deleteApp(appStudioToken, teamsAppId) {
        if (!this.regionEndpoint)
            throw new Error("Failed to get region");
        let requester;
        try {
            requester = this.createRequesterWithToken(appStudioToken);
            globalVars_1.TOOLS.logProvider.debug(`Sent API Request: DELETE ${this.getEndpoint()}/api/appdefinitions/${teamsAppId}`);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.delete(`/api/appdefinitions/${teamsAppId}`));
            if (response && response.data) {
                const success = response.data;
                if (success) {
                    return success;
                }
                else {
                    globalVars_1.TOOLS.logProvider?.error("Cannot get the app definitions");
                }
            }
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.DELETE_APP);
        }
        throw this.wrapException(new Error("cannot delete the app: " + teamsAppId), constants_2.APP_STUDIO_API_NAMES.DELETE_APP);
    }
    async getApp(token, teamsAppId) {
        let requester;
        try {
            requester = this.createRequesterWithToken(token);
            globalVars_1.TOOLS.logProvider.debug(`Sent API Request: GET ${this.getEndpoint()}/api/appdefinitions/${teamsAppId}`);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/appdefinitions/${teamsAppId}`));
            if (response && response.data) {
                const app = response.data;
                if (app && app.teamsAppId && app.teamsAppId === teamsAppId) {
                    return app;
                }
                else {
                    globalVars_1.TOOLS.logProvider?.error(`teamsAppId mismatch. Input: ${teamsAppId}. Got: ${app.teamsAppId}`);
                }
            }
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_APP);
        }
        throw this.wrapException(new Error(`cannot get the app definition with app ID ${teamsAppId}`), constants_2.APP_STUDIO_API_NAMES.GET_APP);
    }
    async getBotId(token, teamsAppId) {
        const app = await this.getApp(token, teamsAppId);
        if (app?.bots?.length && app.bots.length > 0) {
            return app.bots[0].botId;
        }
        globalVars_1.TOOLS.logProvider?.error(`botId not found. Input: ${teamsAppId}`);
        return undefined;
    }
    async getAppPackage(token, teamsAppId) {
        globalVars_1.TOOLS.logProvider?.info("Downloading app package for app " + teamsAppId);
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/appdefinitions/${teamsAppId}/manifest`));
            if (response && response.data) {
                globalVars_1.TOOLS.logProvider?.info("Download app package successfully");
                return response.data;
            }
            else {
                throw this.wrapException(new Error((0, localizeUtils_1.getLocalizedString)("plugins.appstudio.emptyAppPackage", teamsAppId)), constants_2.APP_STUDIO_API_NAMES.GET_APP_PACKAGE);
            }
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_APP_PACKAGE);
        }
    }
    /**
     * Check if app exists in the user's organization by the Teams app id
     * @param teamsAppId
     * @param token
     * @param logProvider
     * @returns
     */
    async checkExistsInTenant(token, teamsAppId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/appdefinitions/manifest/${teamsAppId}`));
            if (response && response.data) {
                return response.data;
            }
            else {
                return false;
            }
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Publish Teams app to Teams App Catalog
     */
    async publishTeamsApp(token, teamsAppId, file) {
        try {
            const requester = this.createRequesterWithToken(token);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/api/publishing", file, {
                headers: { "Content-Type": "application/zip" },
            }));
            if (response && response.data) {
                if (response.data.error) {
                    // To avoid App Studio BadGateway error
                    // The app is actually published to app catalog.
                    if (response.data.error.code === "BadGateway") {
                        const appDefinition = await this.getStaggedApp(token, teamsAppId);
                        if (appDefinition) {
                            return appDefinition.teamsAppId;
                        }
                    }
                    // Corner case
                    // Fail if an app with the same external.id exists in the staged app entitlements
                    // App with same id already exists in the staged apps, Invoke UpdateAPI instead.
                    if (response.data.error.code == "Conflict" &&
                        response.data.error.innerError?.code == "AppDefinitionAlreadyExists") {
                        try {
                            return await this.publishTeamsAppUpdate(token, teamsAppId, file);
                        }
                        catch (e) {
                            if (e instanceof teamsApp_1.DeveloperPortalAPIFailedSystemError) {
                                throw this.wrapException(this.wrapResponse(undefined, response), constants_2.APP_STUDIO_API_NAMES.PUBLISH_APP, errors_1.AppStudioError.TeamsAppPublishConflictError.name, errors_1.AppStudioError.TeamsAppPublishConflictError.message(teamsAppId)[0], errors_1.AppStudioError.TeamsAppPublishConflictError.message(teamsAppId)[1]);
                            }
                            else {
                                throw e;
                            }
                        }
                    }
                    throw this.wrapException(this.wrapResponse(undefined, response), constants_2.APP_STUDIO_API_NAMES.PUBLISH_APP);
                }
                else {
                    return response.data.id;
                }
            }
            else {
                throw this.wrapException(this.wrapResponse(new Error("empty response"), response), constants_2.APP_STUDIO_API_NAMES.PUBLISH_APP, errors_1.AppStudioError.TeamsAppPublishFailedError.name, errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, "POST /api/publishing")[0], errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, "POST /api/publishing")[1]);
            }
        }
        catch (e) {
            if (e instanceof teamsfx_api_1.SystemError) {
                throw e;
            }
            else {
                throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.PUBLISH_APP);
            }
        }
    }
    /**
     * Update existed publish request
     * @param teamsAppId
     * @param file
     * @param token
     * @returns
     */
    async publishTeamsAppUpdate(token, teamsAppId, file) {
        try {
            // Get App Definition from Teams App Catalog
            const appDefinition = await this.getStaggedApp(token, teamsAppId);
            const requester = this.createRequesterWithToken(token);
            let response = null;
            if (appDefinition) {
                // update the existing app
                response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/publishing/${appDefinition.teamsAppId}/appdefinitions`, file, {
                    headers: { "Content-Type": "application/zip" },
                }));
            }
            else {
                throw this.wrapException(new Error("API failed"), constants_2.APP_STUDIO_API_NAMES.GET_PUBLISHED_APP, errors_1.AppStudioError.TeamsAppPublishFailedError.name, errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, `GET /api/publishing/${teamsAppId}`)[0], errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, `GET /api/publishing/${teamsAppId}`)[1]);
            }
            const requestPath = `${response?.request?.method} ${response?.request?.path}`;
            if (response && response.data) {
                if (response.data.error || response.data.errorMessage) {
                    throw this.wrapException(this.wrapResponse(undefined, response), constants_2.APP_STUDIO_API_NAMES.UPDATE_PUBLISHED_APP);
                }
                else {
                    return response.data.teamsAppId;
                }
            }
            else {
                throw this.wrapException(new Error("empty response"), constants_2.APP_STUDIO_API_NAMES.UPDATE_PUBLISHED_APP, errors_1.AppStudioError.TeamsAppPublishFailedError.name, errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, requestPath)[0], errors_1.AppStudioError.TeamsAppPublishFailedError.message(teamsAppId, requestPath)[1]);
            }
        }
        catch (error) {
            if (error instanceof teamsApp_1.DeveloperPortalAPIFailedSystemError) {
                throw error;
            }
            else {
                throw this.wrapException(error, constants_2.APP_STUDIO_API_NAMES.UPDATE_PUBLISHED_APP);
            }
        }
    }
    /**
     * Get Stagged Teams app from tenant app catalog
     * @param teamsAppId manifest.id, which is externalId in app catalog.
     * @param token
     * @returns
     */
    async getStaggedApp(token, teamsAppId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/publishing/${teamsAppId}`));
            if (response && response.data && response.data.value && response.data.value.length > 0) {
                const appdefinitions = response.data.value[0].appDefinitions.map((item) => {
                    return {
                        lastModifiedDateTime: item.lastModifiedDateTime
                            ? new Date(item.lastModifiedDateTime)
                            : null,
                        publishingState: item.publishingState,
                        teamsAppId: item.teamsAppId,
                        displayName: item.displayName,
                    };
                });
                return appdefinitions[appdefinitions.length - 1];
            }
            else {
                return undefined;
            }
        }
        catch (e) {
            return undefined;
        }
    }
    async getUserList(token, teamsAppId) {
        const app = await this.getApp(token, teamsAppId);
        return app.userList;
    }
    async checkPermission(token, teamsAppId, userObjectId) {
        let userList;
        try {
            userList = await this.getUserList(token, teamsAppId);
        }
        catch (error) {
            return constants_2.Constants.PERMISSIONS.noPermission;
        }
        const findUser = userList?.find((user) => user.aadId === userObjectId);
        if (!findUser) {
            return constants_2.Constants.PERMISSIONS.noPermission;
        }
        if (findUser.isAdministrator) {
            return constants_2.Constants.PERMISSIONS.admin;
        }
        else {
            return constants_2.Constants.PERMISSIONS.operative;
        }
    }
    async removePermission(token, teamsAppId, userToRemove) {
        const app = await this.getApp(token, teamsAppId);
        if (!this.checkUser(app, userToRemove)) {
            return;
        }
        app.userList = app.userList?.filter((user) => user["aadId"] !== userToRemove.aadId);
        let requester;
        try {
            globalVars_1.TOOLS.logProvider.debug((0, localizeUtils_1.getLocalizedString)("core.common.SendingApiRequest", `${this.getEndpoint()}/api/appdefinitions/{teamsAppId}/owner`, JSON.stringify(app)));
            requester = this.createRequesterWithToken(token);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/appdefinitions/${teamsAppId}/owner`, app));
            globalVars_1.TOOLS.logProvider.debug((0, localizeUtils_1.getLocalizedString)("core.common.ReceiveApiResponse", JSON.stringify(response?.data)));
            if (!response ||
                !response.data ||
                this.checkUser(response.data, userToRemove)) {
                throw new Error("Response is empty or user is not removed.");
            }
        }
        catch (err) {
            throw this.wrapException(err, constants_2.APP_STUDIO_API_NAMES.UPDATE_OWNER);
        }
    }
    async grantPermission(token, teamsAppId, newUser) {
        const app = await this.getApp(token, teamsAppId);
        if (this.checkUser(app, newUser)) {
            return;
        }
        app.userList?.push(newUser);
        let requester;
        try {
            globalVars_1.TOOLS.logProvider.debug((0, localizeUtils_1.getLocalizedString)("core.common.SendingApiRequest", `${this.getEndpoint()}/api/appdefinitions/{teamsAppId}/owner`, JSON.stringify(app)));
            requester = this.createRequesterWithToken(token);
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/appdefinitions/${teamsAppId}/owner`, app));
            globalVars_1.TOOLS.logProvider.debug((0, localizeUtils_1.getLocalizedString)("core.common.ReceiveApiResponse", JSON.stringify(response?.data)));
            if (!response || !response.data || !this.checkUser(response.data, newUser)) {
                throw new Error(constants_2.ErrorMessages.GrantPermissionFailed);
            }
        }
        catch (err) {
            throw this.wrapException(err, constants_2.APP_STUDIO_API_NAMES.UPDATE_OWNER);
        }
    }
    /**
     * Send the app package for partner center validation
     * @param file
     * @param token
     * @returns
     */
    async partnerCenterAppPackageValidation(token, file) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/api/appdefinitions/partnerCenterAppPackageValidation", file, {
                headers: { "Content-Type": "application/zip" },
            }));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.VALIDATE_APP_PACKAGE);
        }
    }
    checkUser(app, newUser) {
        const findUser = app.userList?.findIndex((user) => user["aadId"] === newUser.aadId);
        if (findUser != undefined && findUser >= 0) {
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * Submit App Validation Request (In-App) for which App Definitions are stored at TDP.
     * @param teamsAppId
     * @param token
     * @param timeoutSeconds
     * @returns
     */
    async submitAppValidationRequest(token, teamsAppId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/v1.0/appvalidations/appdefinition/validate`, {
                AppEnvironmentId: null,
                appDefinitionId: teamsAppId,
            }));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.SUBMIT_APP_VALIDATION);
        }
    }
    /**
     * Get App validation requests sumitted by the user
     */
    async getAppValidationRequestList(token, teamsAppId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/v1.0/appvalidations/appdefinitions/${teamsAppId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_APP_VALIDATION_REQUESTS);
        }
    }
    /**
     * Get App validation results by provided app validation id
     * @param appValidationId
     * @param token
     * @param timeoutSeconds
     * @returns
     */
    async getAppValidationById(token, appValidationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/v1.0/appvalidations/${appValidationId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_APP_VALIDATION_RESULT);
        }
    }
    async getSideloadingStatus(token) {
        const apiName = "<check-sideloading-status>";
        const apiPath = "/api/usersettings/mtUserAppPolicy";
        const requester = this.createRequesterWithToken(token);
        let response = undefined;
        try {
            response = (await retryHandler_1.RetryHandler.Retry(() => requester.get(apiPath)));
            let result;
            if (response.status >= 400) {
                result = undefined;
            }
            else {
                result = response.data?.value?.isSideloadingAllowed;
            }
            if (result !== undefined) {
                (0, telemetry_1.sendTelemetryEvent)("TeamsDevPortalClient", telemetry_1.TelemetryEvent.CheckSideloading, {
                    [telemetry_1.TelemetryProperty.IsSideloadingAllowed]: result.toString() + "",
                });
            }
            else {
                (0, telemetry_1.sendTelemetryErrorEvent)("TeamsDevPortalClient", telemetry_1.TelemetryEvent.CheckSideloading, new teamsfx_api_1.SystemError("M365Account", "UnknownValue", `AppStudio response code: ${response.status}, body: ${response.data}`), {
                    [telemetry_1.TelemetryProperty.CheckSideloadingStatusCode]: `${response.status}`,
                    [telemetry_1.TelemetryProperty.CheckSideloadingMethod]: "get",
                    [telemetry_1.TelemetryProperty.CheckSideloadingUrl]: apiName,
                });
            }
            return result;
        }
        catch (error) {
            (0, telemetry_1.sendTelemetryErrorEvent)("TeamsDevPortalClient", telemetry_1.TelemetryEvent.CheckSideloading, new teamsApp_1.CheckSideloadingPermissionFailedError(error, error.response?.headers?.[constants_2.Constants.CORRELATION_ID] ?? "", apiName, (0, localizeUtils_1.getDefaultString)("error.appstudio.apiFailed.reason.common", error.response?.data ? `data: ${JSON.stringify(error.response.data)}` : "")), {
                [telemetry_1.TelemetryProperty.CheckSideloadingStatusCode]: `${error?.response?.status}`,
                [telemetry_1.TelemetryProperty.CheckSideloadingMethod]: "get",
                [telemetry_1.TelemetryProperty.CheckSideloadingUrl]: apiName,
            });
        }
        return undefined;
    }
    /**
     * Create the Api Key registration.
     * @param token
     * @param apiKeyRegistration
     */
    async createApiKeyRegistration(token, apiKeyRegistration) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/api/v1.0/apiSecretRegistrations", apiKeyRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_API_KEY);
        }
    }
    /**
     * Get the Api Key registration by Id.
     * @param token
     * @param apiSecretRegistrationId
     */
    async getApiKeyRegistrationById(token, apiSecretRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/v1.0/apiSecretRegistrations/${apiSecretRegistrationId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_API_KEY);
        }
    }
    async updateApiKeyRegistration(token, apiKeyRegistration, apiKeyRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.patch(`/api/v1.0/apiSecretRegistrations/${apiKeyRegistrationId}`, apiKeyRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.UPDATE_API_KEY);
        }
    }
    async getOauthRegistrationById(token, oauthRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/v1.0/oAuthConfigurations/${oauthRegistrationId}`));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.GET_OAUTH);
        }
    }
    async createOauthRegistration(token, oauthRegistration) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post("/api/v1.0/oAuthConfigurations", oauthRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.CREATE_OAUTH);
        }
    }
    async updateOauthRegistration(token, oauthRegistration, oauthRegistrationId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.patch(`/api/v1.0/oAuthConfigurations/${oauthRegistrationId}`, oauthRegistration));
            return response?.data;
        }
        catch (e) {
            throw this.wrapException(e, constants_2.APP_STUDIO_API_NAMES.UPDATE_OAUTH);
        }
    }
    async getBotRegistration(token, botId) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get(`/api/botframework/${botId}`));
            if ((0, common_1.isHappyResponse)(response)) {
                return response.data; // response cannot be undefined as it's checked in isHappyResponse.
            }
            else {
                // Defensive code and it should never reach here.
                throw this.wrapException(this.wrapResponse(undefined, response), constants_2.APP_STUDIO_API_NAMES.GET_BOT, (0, localizeUtils_1.getDefaultString)("error.appstudio.apiFailed.name.common"), "Failed to get data");
            }
        }
        catch (e) {
            this.handleBotFrameworkError(e, constants_2.APP_STUDIO_API_NAMES.GET_BOT);
        }
    }
    async listBots(token) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.get("/api/botframework"));
            if ((0, common_1.isHappyResponse)(response)) {
                return response.data; // response cannot be undefined as it's checked in isHappyResponse.
            }
            else {
                // Defensive code and it should never reach here.
                throw this.wrapException(this.wrapResponse(undefined, response), constants_2.APP_STUDIO_API_NAMES.LIST_BOT, (0, localizeUtils_1.getDefaultString)("error.appstudio.apiFailed.name.common"), "Failed to get data");
            }
        }
        catch (e) {
            this.handleBotFrameworkError(e, constants_2.APP_STUDIO_API_NAMES.LIST_BOT);
        }
    }
    async deleteBot(token, botId) {
        const requester = this.createRequesterWithToken(token);
        try {
            await retryHandler_1.RetryHandler.Retry(() => requester.delete(`/api/botframework/${botId}`));
        }
        catch (e) {
            this.handleBotFrameworkError(e, constants_2.APP_STUDIO_API_NAMES.DELETE_BOT);
        }
    }
    async createBotRegistration(token, registration, checkExistence = true) {
        if (registration.botId && checkExistence) {
            const botReg = await this.getBotRegistration(token, registration.botId);
            if (botReg) {
                globalVars_1.TOOLS.logProvider.info(messages_1.Messages.BotResourceExist("Appstudio"));
                return;
            }
        }
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/botframework`, registration));
            if (!(0, common_1.isHappyResponse)(response)) {
                throw new errors_2.ProvisionError(strings_1.CommonStrings.APP_STUDIO_BOT_REGISTRATION);
            }
        }
        catch (e) {
            this.handleBotFrameworkError(e, constants_2.APP_STUDIO_API_NAMES.CREATE_BOT);
        }
    }
    async updateMessageEndpoint(token, botId, endpoint) {
        const botReg = await this.getBotRegistration(token, botId);
        if (!botReg) {
            throw new errors_2.BotRegistrationNotFoundError(botId);
        }
        botReg.messagingEndpoint = endpoint;
        if (botReg.configuredChannels === undefined || botReg.configuredChannels.length === 0) {
            botReg.configuredChannels = [IBotRegistration_1.BotChannelType.MicrosoftTeams];
        }
        await this.updateBotRegistration(token, botReg);
    }
    async updateBotRegistration(token, botReg) {
        const requester = this.createRequesterWithToken(token);
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/botframework/${botReg.botId}`, botReg));
            if (!(0, common_1.isHappyResponse)(response)) {
                throw new errors_2.ConfigUpdatingError(strings_1.ConfigNames.MESSAGE_ENDPOINT);
            }
        }
        catch (e) {
            this.handleBotFrameworkError(e, constants_2.APP_STUDIO_API_NAMES.UPDATE_BOT);
        }
    }
    async createAADApp(token, displayName, signInAudience = signInAudience_1.SignInAudience.AzureADMyOrg, serviceManagementReference, isMicrosoftUser = false) {
        const requester = this.createRequesterWithToken(token);
        const requestBody = {
            displayName: displayName,
            signInAudience: signInAudience,
            serviceManagementReference: serviceManagementReference,
        }; // Create a Microsoft Entra app and optionally set service tree id
        try {
            const response = await retryHandler_1.RetryHandler.Retry(() => requester.post(`/api/aadapp/v2`, requestBody));
            if (response && response.data) {
                return response.data;
            }
        }
        catch (err) {
            if (axios_1.default.isAxiosError(err) && err.response) {
                if (err.response.data?.error?.code === constants_4.aadErrorCode.signInAudienceNotAllowedAsPerAppPolicy) {
                    throw new signInAudienceNotAllowedError_1.SignInAudienceNotAllowedError("TeamsDevPortalClient", err.response.data.error?.message, isMicrosoftUser);
                }
            }
            throw this.wrapException(err, constants_2.APP_STUDIO_API_NAMES.CREATE_AAD_APP);
        }
        throw this.wrapException(new Error(`Failed to create AAD app: ${displayName}`), constants_2.APP_STUDIO_API_NAMES.CREATE_AAD_APP);
    }
    handleBotFrameworkError(e, apiName) {
        if (e.response?.status === commonConstant_1.HttpStatusCode.NOTFOUND) {
            return undefined; // Stands for NotFound.
        }
        else if (e.response?.status === commonConstant_1.HttpStatusCode.UNAUTHORIZED) {
            throw new errors_2.BotFrameworkNotAllowedToAcquireTokenError();
        }
        else if (e.response?.status === commonConstant_1.HttpStatusCode.FORBIDDEN) {
            throw new errors_2.BotFrameworkForbiddenResultError(e);
        }
        else if (e.response?.status === commonConstant_1.HttpStatusCode.TOOMANYREQS) {
            throw new errors_2.BotFrameworkConflictResultError();
        }
        else {
            e.teamsfxUrlName = constants_3.TeamsFxUrlNames[apiName];
            throw this.wrapException(e, apiName);
        }
    }
    wrapResponse(e, response) {
        const error = new Error(e?.message || response?.data.error?.message || response?.data.errorMessage);
        error.response = response;
        error.request = response?.request;
        return error;
    }
    wrapException(e, apiName, name = (0, localizeUtils_1.getDefaultString)("error.appstudio.apiFailed.name.common"), potentialReason = (0, localizeUtils_1.getDefaultString)("error.appstudio.apiFailed.reason.common"), disPlayMessage, isUserError = false, helpLink) {
        e.name = name;
        const correlationId = e.response?.headers?.[constants_2.Constants.CORRELATION_ID];
        let extraData = `${potentialReason} ${e.response?.data ? `data: ${JSON.stringify(e.response.data)}` : ""}`;
        // add status code in extra data if the message does not have it.
        if (!e.message?.toLowerCase().includes("status code") && e.response?.status) {
            extraData = `Status code: ${e.response.status}. ${extraData}`;
        }
        let error;
        if (isUserError) {
            error = new teamsApp_1.DeveloperPortalAPIFailedUserError(e, correlationId, apiName, extraData, disPlayMessage, helpLink);
        }
        else {
            error = new teamsApp_1.DeveloperPortalAPIFailedSystemError(e, correlationId, apiName, extraData, disPlayMessage);
        }
        return error;
    }
}
exports.TeamsDevPortalClient = TeamsDevPortalClient;
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Buffer, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "importApp", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "listApps", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "deleteApp", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getApp", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getBotId", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getAppPackage", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "checkExistsInTenant", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Buffer]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "publishTeamsApp", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Buffer]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "publishTeamsAppUpdate", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getStaggedApp", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getUserList", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "checkPermission", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "removePermission", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "grantPermission", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Buffer]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "partnerCenterAppPackageValidation", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "submitAppValidationRequest", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getAppValidationRequestList", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getAppValidationById", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getSideloadingStatus", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "createApiKeyRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getApiKeyRegistrationById", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "updateApiKeyRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getOauthRegistrationById", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "createOauthRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "updateOauthRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "getBotRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "listBots", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "deleteBot", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "createBotRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "updateMessageEndpoint", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "updateBotRegistration", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: "Teams", component: "TeamsDevPortalClient" })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], TeamsDevPortalClient.prototype, "createAADApp", null);
exports.teamsDevPortalClient = new TeamsDevPortalClient();
//# sourceMappingURL=teamsDevPortalClient.js.map